import _ from 'lodash';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_GRID_DIMENSION,
  DEFAULT_LEARNING_RATE,
  DEFAULT_NUM_ITERATIONS,
  DEFAULT_RANDOM_ORDER,
  DEFAULT_SIGMA,
  INPUT_NAMES,
} from '@/tools/selfOrganizingMapsV2/selfOrganizingMaps.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { MappedInputFeatureV1 } from '@/tools/models/templateInstance.utilities';
import type {
  BaseToolPersistedState,
  ModelRehydratePayload,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
} from '@/tools/tool.types';

export interface SelfOrganizingMapsV2StoreState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  outputsCollapsed: boolean;
  modelPropertiesExpanded: boolean;
  color: string;
  anomalyThreshold: number;
  sigma: number;
  learningRate: number;
  gridDimension: number | null;
  numIterations: number | null;
  randomOrder: boolean;
  cleanCapsules: boolean;
  capsuleMinDuration: { value: number; units: string };
  capsuleMergeDuration: { value: number; units: string };
  inputSignals: ToolItem[];
  condition?: ToolItem | null;
  restrictToCondition?: ToolItem | null;
  tableApplicationId?: string;
  instanceId?: string;
}

export interface SelfOrganizingMapsV2StoreActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setOutputsCollapsed: (collapsed: boolean) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setModelPropertiesExpanded: (expanded: boolean) => void;
  setAnomalyThreshold: (anomalyThreshold: number) => void;
  setSigma: (sigma: number) => void;
  setLearningRate: (learningRate: number) => void;
  setGridDimension: (gridDimension: number | null) => void;
  setNumIterations: (numIterations: number | null) => void;
  setRandomOrder: (randomOrder: boolean) => void;
  setCleanCapsules: (cleanCapsules: boolean) => void;
  setCapsuleMinDuration: (duration: { value: number; units: string }) => void;
  setCapsuleMergeDuration: (duration: { value: number; units: string }) => void;
  addInputSignal: (signal: ToolItem) => void;
  removeInputSignal: (signal: ToolItem) => void;
  setCondition: (condition: ToolItem | null | undefined) => void;
  setRestrictToCondition: (condition: ToolItem | null | undefined) => void;
  rehydrateForEdit: (payload: ModelRehydratePayload) => void;
  reset: ResettableStoreState<SelfOrganizingMapsV2StoreState>;
}

export type SelfOrganizingMapsDehydratedState = SelfOrganizingMapsV2StoreState;

export const DEFAULT_DURATION = { value: 0, units: 'min' };

const ITEM_PARAMETER_KEYS = ['inputSignals', 'condition', 'restrictToCondition'];

const initialState: SelfOrganizingMapsV2StoreState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  windowStart: 0,
  windowEnd: 0,
  outputsCollapsed: true,
  modelPropertiesExpanded: false,
  color: '',
  anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
  sigma: DEFAULT_SIGMA,
  learningRate: DEFAULT_LEARNING_RATE,
  gridDimension: DEFAULT_GRID_DIMENSION,
  numIterations: DEFAULT_NUM_ITERATIONS,
  randomOrder: DEFAULT_RANDOM_ORDER,
  cleanCapsules: false,
  capsuleMinDuration: { value: 0, units: 'min' },
  capsuleMergeDuration: { value: 0, units: 'min' },
  inputSignals: [] as ToolItem[],
  condition: undefined as ToolItem | null | undefined,
  restrictToCondition: undefined as ToolItem | null | undefined,
  tableApplicationId: undefined as string | undefined,
  instanceId: undefined as string | undefined,
});

const selfOrganizingMapsV2ConfigParamsSelector = createSelector(
  [(state: SelfOrganizingMapsV2StoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.SELF_ORGANIZING_MAPS_V_2,
      parameterDefinitionKeys: ITEM_PARAMETER_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return _.omit(config, ['modelPropertiesExpanded']) as ToolConfigParams;
  },
);

const selfOrganizingMapsV2WindowSelector = createSelector(
  [
    (state: SelfOrganizingMapsV2StoreState) => state.windowStart,
    (state: SelfOrganizingMapsV2StoreState) => state.windowEnd,
  ],
  (windowStart, windowEnd) => ({ startTime: windowStart, endTime: windowEnd }),
);

export const dehydrateSelfOrganizingMapsV2StoreState = (
  state: SelfOrganizingMapsV2StoreState,
): SelfOrganizingMapsDehydratedState => state;

const selfOrganizingMapsV2ActionsFactory: ActionsFactory<
  SelfOrganizingMapsV2StoreState,
  SelfOrganizingMapsV2StoreActions
> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setOutputsCollapsed: (collapsed) =>
    set((draft) => {
      draft.outputsCollapsed = collapsed;
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setModelPropertiesExpanded: (expanded) =>
    set((draft) => {
      draft.modelPropertiesExpanded = expanded;
    }),

  setAnomalyThreshold: (anomalyThreshold) =>
    set((draft) => {
      draft.anomalyThreshold = anomalyThreshold;
    }),

  setSigma: (sigma) =>
    set((draft) => {
      draft.sigma = sigma;
    }),

  setLearningRate: (learningRate) =>
    set((draft) => {
      draft.learningRate = learningRate;
    }),

  setGridDimension: (gridDimension) =>
    set((draft) => {
      draft.gridDimension = gridDimension;
    }),

  setNumIterations: (numIterations) =>
    set((draft) => {
      draft.numIterations = numIterations;
    }),

  setRandomOrder: (randomOrder) =>
    set((draft) => {
      draft.randomOrder = randomOrder;
    }),

  setCleanCapsules: (cleanCapsules) =>
    set((draft) => {
      draft.cleanCapsules = cleanCapsules;
    }),

  setCapsuleMinDuration: (duration) =>
    set((draft) => {
      draft.capsuleMinDuration = duration;
    }),

  setCapsuleMergeDuration: (duration) =>
    set((draft) => {
      draft.capsuleMergeDuration = duration;
    }),

  addInputSignal: (signal) =>
    set((draft) => {
      const sanitized = sanitizeToolItem(signal);
      if (sanitized && !draft.inputSignals.some((s) => s.id === sanitized.id)) {
        draft.inputSignals = draft.inputSignals.concat(sanitized);
      }
    }),

  removeInputSignal: (signal) =>
    set((draft) => {
      draft.inputSignals = draft.inputSignals.filter((s) => s.id !== signal?.id);
    }),

  setCondition: (item) =>
    set((draft) => {
      draft.condition = sanitizeToolItem(item) as SelfOrganizingMapsV2StoreState['condition'];
    }),

  setRestrictToCondition: (item) =>
    set((draft) => {
      draft.restrictToCondition = sanitizeToolItem(item) as SelfOrganizingMapsV2StoreState['restrictToCondition'];
    }),

  rehydrateForEdit: (payload: ModelRehydratePayload) => {
    resetState();
    const inputSignals: ToolItem[] = [];
    set((draft) => {
      draft.id = payload.id;
      draft.instanceId = payload.instanceId;
      draft.tableApplicationId = payload.instanceData?.tableApplicationId;
      (payload.modelData?.inputFeatures ?? []).forEach((feature: MappedInputFeatureV1): void => {
        const inputName: string = feature.name!;
        switch (inputName) {
          case SeeqNames.Models.InstanceNameInputFeatureName:
            // TODO: CRAB-53864: Handle duplicate names more gracefully
            draft.name = payload.instanceId ? feature.inputText : `${feature.inputText} (Copy)`;
            break;
          case INPUT_NAMES.anomalyThresholdInput:
            draft.anomalyThreshold = feature.inputNumeric ?? DEFAULT_ANOMALY_THRESHOLD;
            break;
          case INPUT_NAMES.sigmaInput:
            draft.sigma = feature.inputNumeric ?? DEFAULT_SIGMA;
            break;
          case INPUT_NAMES.learningRateInput:
            draft.learningRate = feature.inputNumeric ?? DEFAULT_LEARNING_RATE;
            break;
          case INPUT_NAMES.gridDimensionInput:
            draft.gridDimension = feature.inputNumeric ?? DEFAULT_GRID_DIMENSION;
            break;
          case INPUT_NAMES.numIterationsInput:
            draft.numIterations = feature.inputNumeric ?? DEFAULT_NUM_ITERATIONS;
            break;
          case INPUT_NAMES.randomOrderInput:
            draft.randomOrder = feature.inputBoolean ?? DEFAULT_RANDOM_ORDER;
            break;
          case INPUT_NAMES.startTimeInput: {
            const startTimeInput = JSON.parse(feature.inputText!);
            draft.windowStart = moment(startTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.endTimeInput: {
            const endTimeInput = JSON.parse(feature.inputText!);
            draft.windowEnd = moment(endTimeInput).valueOf();
            break;
          }
          case INPUT_NAMES.mergeCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMergeDuration = {
              value: Number(feature.inputNumeric ?? 0),
              units: feature.inputNumericUom ?? 'min',
            };
            break;
          }
          case INPUT_NAMES.minCapsuleDurationInput: {
            draft.cleanCapsules = !_.isNil(feature.inputNumeric);
            draft.capsuleMinDuration = {
              value: Number(feature.inputNumeric ?? 0),
              units: feature.inputNumericUom ?? 'min',
            };
            break;
          }
          case INPUT_NAMES.trainWithinConditionInput:
            if (feature.inputItem) {
              draft.condition = { id: feature.inputItem };
            }
            break;
          case INPUT_NAMES.restrictOutputToConditionInput: {
            if (feature.inputItem) {
              draft.restrictToCondition = { id: feature.inputItem };
            }
            break;
          }
          default:
            if (inputName.startsWith(INPUT_NAMES.signalInput) && feature.inputItem) {
              inputSignals.push({ id: feature.inputItem });
            }
            break;
        }
      });
      draft.inputSignals = inputSignals;
    });
  },

  reset: (overrides) => resetState(overrides),
});

const [SelfOrganizingMapsV2StoreProvider, useSelfOrganizingMapsV2StoreInstance, useSelfOrganizingMapsV2StoreSelector] =
  createStore('SelfOrganizingMapsV2', initialState, selfOrganizingMapsV2ActionsFactory);

export { SelfOrganizingMapsV2StoreProvider };

export const useSelfOrganizingMapsV2ToolId = () => useSelfOrganizingMapsV2StoreSelector((state) => state.id);
export const useSelfOrganizingMapsV2ToolName = () => useSelfOrganizingMapsV2StoreSelector((state) => state.name);
export const useSelfOrganizingMapsV2ToolOriginalParameters = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.originalParameters);
export const useSelfOrganizingMapsV2ToolAdvancedParametersCollapsed = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.advancedParametersCollapsed);
export const useSelfOrganizingMapsV2ToolOutputsCollapsed = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.outputsCollapsed);
export const useSelfOrganizingMapsV2ToolWindow = () =>
  useSelfOrganizingMapsV2StoreSelector(selfOrganizingMapsV2WindowSelector);
export const useSelfOrganizingMapsV2ToolModelPropertiesExpanded = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.modelPropertiesExpanded);
export const useSelfOrganizingMapsV2ToolAnomalyThreshold = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.anomalyThreshold);
export const useSelfOrganizingMapsV2ToolSigma = () => useSelfOrganizingMapsV2StoreSelector((state) => state.sigma);
export const useSelfOrganizingMapsV2ToolLearningRate = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.learningRate);
export const useSelfOrganizingMapsV2ToolGridDimension = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.gridDimension);
export const useSelfOrganizingMapsV2ToolNumIterations = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.numIterations);
export const useSelfOrganizingMapsV2ToolRandomOrder = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.randomOrder);
export const useSelfOrganizingMapsV2ToolCleanCapsules = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.cleanCapsules);
export const useSelfOrganizingMapsV2ToolCapsuleMinDuration = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.capsuleMinDuration);
export const useSelfOrganizingMapsV2ToolCapsuleMergeDuration = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.capsuleMergeDuration);
export const useSelfOrganizingMapsV2ToolInputSignals = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.inputSignals);
export const useSelfOrganizingMapsV2ToolCondition = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.condition);
export const useSelfOrganizingMapsV2ToolRestrictToCondition = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.restrictToCondition);
export const useSelfOrganizingMapsV2ToolInstanceId = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.instanceId);
export const useSelfOrganizingMapsV2ToolTableApplicationId = () =>
  useSelfOrganizingMapsV2StoreSelector((state) => state.tableApplicationId);
export const useSelfOrganizingMapsV2ToolConfigParams = () =>
  useSelfOrganizingMapsV2StoreSelector(selfOrganizingMapsV2ConfigParamsSelector);

export const useSelfOrganizingMapsV2ToolActions = () => useSelfOrganizingMapsV2StoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestSelfOrganizingMapsV2Store(overrides?: Partial<SelfOrganizingMapsV2StoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestSelfOrganizingMapsV2Store() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, selfOrganizingMapsV2ActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (utility files)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateSelfOrganizingMapsV2Store() {
  return createStoreInstance<SelfOrganizingMapsV2StoreState, SelfOrganizingMapsV2StoreActions>(
    initialState,
    selfOrganizingMapsV2ActionsFactory,
  );
}

export const usePersistSelfOrganizingMapsV2Store = () => {
  const register = useRegisterPersistedStore();
  const store = useSelfOrganizingMapsV2StoreInstance();

  useEffect(() => {
    return register({
      storeName: 'selfOrganizingMapsV2Store',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateSelfOrganizingMapsV2StoreState,
    });
  }, [register, store]);
};
